import { projectAtom } from '@data/auth';
import type {
  DrivingDailyRoute,
  DrivingRoute,
  DrivingRouteTask,
} from '@data/datahub/drivingRouteData/types';
import { environmentAtom } from '@data/fms/environment/states';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosError, AxiosResponse } from 'axios';
import axios from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Result } from '..';
import { Failure, Success, useDatahubAPI, useErrorMessage } from '..';

type DrivingRouteResponse = {
  route_metrics: DrivingRoute[];
  page_size: number;
  next_page_token?: string;
};

export const useDrivingRouteAPI = () => {
  const [loading, setLoading] = useState(false);
  const { notifyError } = useNotification();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();
  const { datahubAPI } = useDatahubAPI({ cancelWhenUnmounting: true });

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      areaMapVersionId: string,
      params: URLSearchParams,
    ): Promise<Result<DrivingRouteResponse, AxiosResponse>> => {
      try {
        const res = await datahubAPI.get(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/area_map_version_ids/${areaMapVersionId}/metrics?${params.toString()}`,
        );
        return new Success(res?.data);
      } catch (error) {
        return axios.isCancel(error)
          ? new Success({
              route_metrics: [],
              page_size: 0,
            })
          : new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [datahubAPI],
  );

  const getDrivingRoute = useAtomCallback(
    useCallback(
      async (
        get,
        _,
        vehicleId: string,
        areaMapVersionId: string,
        params: URLSearchParams,
      ) => {
        setLoading(true);
        const project = get(projectAtom);
        const environment = get(environmentAtom);

        if (!project || !environment) {
          notifyError({
            message: t('api.datahub.get_driving_route_data', {
              status: 'failed',
            }),
          });
          setLoading(false);
          return [];
        }

        const req = async (requestParams: URLSearchParams) =>
          await request(
            project.id,
            environment.environment_id,
            vehicleId,
            areaMapVersionId,
            requestParams,
          );

        const splitRequest = async (
          data: DrivingRoute[] = [],
        ): Promise<DrivingRoute[]> => {
          const res = await req(params);
          // 取得失敗時
          if (!res.value || res.isFailure()) {
            notifyError({
              message: `${t('api.datahub.get_driving_route_data', {
                status: 'failed',
              })}: ${getErrorMessage(res)}`,
            });
            return data;
          }
          // 取得成功時
          if (res?.isSuccess()) {
            // 配列を結合
            const combinedData = [...data, ...res.value.route_metrics];
            if (isNullOrUndefined(res.value.next_page_token)) {
              return combinedData;
            }
            // レスポンスに next_page_token がある場合は次のリクエストを行う
            params.set('page_token', res.value.next_page_token);
            await splitRequest(combinedData);
          }
          return data;
        };

        const result = await splitRequest();
        setLoading(false);
        return result;
      },
      [notifyError, request, t, getErrorMessage],
    ),
  );

  return {
    loading,
    getDrivingRoute,
  };
};

type DrivingDailyRouteResponse = {
  daily_metrics: DrivingDailyRoute[];
  page_size: number;
  next_page_token?: string;
};

export const useDrivingDailyRouteAPI = () => {
  const [loading, setLoading] = useState(false);
  const { notifyError } = useNotification();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();
  const { datahubAPI } = useDatahubAPI({ cancelWhenUnmounting: true });

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      areaMapVersionId: string,
      routeId: string,
      params: URLSearchParams,
    ): Promise<Result<DrivingDailyRouteResponse, AxiosResponse>> => {
      try {
        const res = await datahubAPI.get(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/area_map_version_ids/${areaMapVersionId}/route_ids/${routeId}/metrics?${params.toString()}`,
        );
        return new Success(res?.data);
      } catch (error) {
        return axios.isCancel(error)
          ? new Success({
              daily_metrics: [],
              page_size: 0,
            })
          : new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [datahubAPI],
  );

  const getDrivingDailyRoute = useAtomCallback(
    useCallback(
      async (
        get,
        _,
        vehicleId: string,
        areaMapVersionId: string,
        routeId: string,
        params: URLSearchParams,
      ) => {
        setLoading(true);
        const project = get(projectAtom);
        const environment = get(environmentAtom);

        if (!project || !environment) {
          notifyError({
            message: t('api.datahub.get_driving_route_data', {
              status: 'failed',
            }),
          });
          setLoading(false);
          return [];
        }

        const req = async (requestParams: URLSearchParams) =>
          await request(
            project.id,
            environment.environment_id,
            vehicleId,
            areaMapVersionId,
            routeId,
            requestParams,
          );

        const splitRequest = async (
          data: DrivingDailyRoute[] = [],
        ): Promise<DrivingDailyRoute[]> => {
          const res = await req(params);
          // 取得失敗時
          if (!res.value || res.isFailure()) {
            notifyError({
              message: `${t('api.datahub.get_driving_route_data', {
                status: 'failed',
              })}: ${getErrorMessage(res)}`,
            });
            return data;
          }
          // 取得成功時
          if (res?.isSuccess()) {
            // 配列を結合
            const combinedData = [...data, ...res.value.daily_metrics];
            if (isNullOrUndefined(res.value.next_page_token)) {
              return combinedData;
            }
            // レスポンスに next_page_token がある場合は次のリクエストを行う
            params.set('page_token', res.value.next_page_token);
            await splitRequest(combinedData);
          }
          return data;
        };

        const result = await splitRequest();
        setLoading(false);
        return result;
      },
      [notifyError, request, t, getErrorMessage],
    ),
  );

  return {
    loading,
    getDrivingDailyRoute,
  };
};

type DrivingRouteTaskResponse = {
  tasks: DrivingRouteTask[];
  page_size: number;
  next_page_token?: string;
};

export const useDrivingRouteTaskAPI = () => {
  const [loading, setLoading] = useState(false);
  const { notifyError } = useNotification();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();
  const { datahubAPI } = useDatahubAPI({ cancelWhenUnmounting: true });

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      areaMapVersionId: string,
      routeId: string,
      params: URLSearchParams,
    ): Promise<Result<DrivingRouteTaskResponse, AxiosResponse>> => {
      try {
        const res = await datahubAPI.get(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/area_map_version_ids/${areaMapVersionId}/route_ids/${routeId}/tasks?${params.toString()}`,
        );
        return new Success(res?.data);
      } catch (error) {
        return axios.isCancel(error)
          ? new Success({
              tasks: [],
              page_size: 0,
            })
          : new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [datahubAPI],
  );

  const getDrivingRouteTask = useAtomCallback(
    useCallback(
      async (
        get,
        _,
        vehicleId: string,
        areaMapVersionId: string,
        routeId: string,
        params: URLSearchParams,
      ) => {
        setLoading(true);
        const project = get(projectAtom);
        const environment = get(environmentAtom);

        if (!project || !environment) {
          notifyError({
            message: t('api.datahub.get_driving_route_data', {
              status: 'failed',
            }),
          });
          setLoading(false);
          return [];
        }

        const req = async (requestParams: URLSearchParams) =>
          await request(
            project.id,
            environment.environment_id,
            vehicleId,
            areaMapVersionId,
            routeId,
            requestParams,
          );

        const splitRequest = async (
          data: DrivingRouteTask[] = [],
        ): Promise<DrivingRouteTask[]> => {
          const res = await req(params);
          // 取得失敗時
          if (!res.value || res.isFailure()) {
            notifyError({
              message: `${t('api.datahub.get_driving_route_data', {
                status: 'failed',
              })}: ${getErrorMessage(res)}`,
            });
            return data;
          }
          // 取得成功時
          if (res?.isSuccess()) {
            // 配列を結合
            const combinedData = [...data, ...res.value.tasks];
            if (isNullOrUndefined(res.value.next_page_token)) {
              return combinedData;
            }
            // レスポンスに next_page_token がある場合は次のリクエストを行う
            params.set('page_token', res.value.next_page_token);
            await splitRequest(combinedData);
          }
          return data;
        };

        const result = await splitRequest();
        setLoading(false);
        return result;
      },
      [notifyError, request, t, getErrorMessage],
    ),
  );

  return {
    loading,
    getDrivingRouteTask,
  };
};
